/**
 * Targeted IAP upgrade flow instrumentation — logs only, no behavior changes.
 */

'use client'

import { isCapacitorIosApp } from '@/lib/auth/capacitorSignIn'
import { detectNativeIosForUpgrade } from '@/lib/monetization/detectUpgradePlatform'
import { isNativeCapacitorApp } from '@/lib/pwa/isNativeCapacitorApp'

const PREFIX = '[UpgradeIAPTrace]'

export function logUpgradeIapTrace(
  step: string,
  detail?: Record<string, unknown>
): void {
  if (detail) {
    console.log(`${PREFIX} ${step} ${JSON.stringify(detail)}`)
  } else {
    console.log(`${PREFIX} ${step}`)
  }
}

export function logUpgradeIapBlocked(
  reason: string,
  detail?: Record<string, unknown>
): void {
  console.log(
    `${PREFIX} blocked before RevenueCat ${JSON.stringify({ reason, ...detail })}`
  )
}

type CustomerInfoLike = {
  entitlements?: {
    active?: Record<string, unknown>
    all?: Record<string, unknown>
  }
  activeSubscriptions?: string[]
  allPurchasedProductIdentifiers?: string[]
  originalAppUserId?: string
  managementURL?: string | null
} | null | undefined

/** Redact/summarize RevenueCat customerInfo for upgrade trace logs only. */
export function summarizeCustomerInfoForUpgradeTrace(
  customerInfo: CustomerInfoLike
): Record<string, unknown> {
  const active = customerInfo?.entitlements?.active ?? {}
  const all = customerInfo?.entitlements?.all ?? {}
  const activeSubscriptions = customerInfo?.activeSubscriptions ?? []
  return {
    activeEntitlementKeys: Object.keys(active),
    allEntitlementKeys: Object.keys(all),
    activeSubscriptions,
    allPurchasedProductIdentifiers: customerInfo?.allPurchasedProductIdentifiers ?? [],
    productIdentifierFromActiveSubscription: activeSubscriptions[0] ?? null,
    originalAppUserId: customerInfo?.originalAppUserId
      ? `${customerInfo.originalAppUserId.slice(0, 8)}…`
      : null,
    hasManagementUrl: Boolean(customerInfo?.managementURL),
  }
}

/** Session + native detection snapshot at upgrade button tap. */
export function snapshotUpgradeTapContext(
  sessionStatus: string,
  sessionUserId?: string | null
): Record<string, unknown> {
  const cap =
    typeof window !== 'undefined'
      ? (window as Window & { Capacitor?: { getPlatform?: () => string } }).Capacitor
      : null

  return {
    sessionStatus,
    userId: sessionUserId ? `${sessionUserId.slice(0, 8)}…` : null,
    isNativeIos: detectNativeIosForUpgrade(),
    isCapacitorIosApp: isCapacitorIosApp(),
    isNativeCapacitorApp: isNativeCapacitorApp(),
    capacitorPlatform: cap?.getPlatform?.() ?? null,
    href: typeof window !== 'undefined' ? window.location.href : null,
  }
}
